/**
 * Merchant · Coupon Checkout Verification
 *
 * COUPON-ELIGIBILITY-1 · Phase A — Merchant surface.
 *
 * Route: /merchant/checkout-verification
 *
 * Lists every storefront on the merchant's account with its current
 * verification status. Merchants can submit / resubmit a cart-or-checkout
 * URL + written instructions per storefront. Screenshot evidence is
 * deferred (no new upload pipeline in Phase A).
 *
 * Status vocabulary (kept 1:1 with backend):
 *   NOT_CHECKED     — no submission yet
 *   PENDING_REVIEW  — merchant submitted, admin reviewing
 *   VERIFIED        — admin confirmed a working coupon-entry field
 *   NOT_SUPPORTED   — admin found no coupon-entry field
 *   GRANDFATHERED   — legacy campaign coverage only (NOT "verified")
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ShieldCheck, ShieldAlert, Clock, Sparkles, X, ExternalLink, Info,
  RefreshCw, MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  NOT_CHECKED: {
    label: "Verification required",
    chip: "bg-amber-50 text-amber-800 ring-amber-200",
    icon: ShieldAlert,
    tone: "amber",
    describe:
      "Submit your cart or checkout URL to enable Coupon Code offers " +
      "for this storefront.",
  },
  PENDING_REVIEW: {
    label: "Under review",
    chip: "bg-sky-50 text-sky-800 ring-sky-200",
    icon: Clock,
    tone: "sky",
    describe:
      "We've received your checkout details. A Cyannect reviewer will " +
      "confirm the coupon-entry field within one business day.",
  },
  VERIFIED: {
    label: "Checkout verified",
    chip: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    icon: ShieldCheck,
    tone: "emerald",
    describe:
      "Your storefront can publish Coupon Code offers using this " +
      "verified checkout destination.",
  },
  NOT_SUPPORTED: {
    label: "Coupon Code not supported",
    chip: "bg-rose-50 text-rose-800 ring-rose-200",
    icon: ShieldAlert,
    tone: "rose",
    describe:
      "We couldn't verify a shopper-facing field where customers can " +
      "enter a coupon or promotional code.",
  },
  GRANDFATHERED: {
    label: "Legacy campaign — verification not yet completed",
    chip: "bg-slate-100 text-slate-700 ring-slate-200",
    icon: Info,
    tone: "slate",
    describe:
      "Grandfathered coverage keeps existing legacy campaigns live. " +
      "You'll need to submit and verify this checkout before publishing " +
      "any new Coupon Code offers.",
  },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.NOT_CHECKED;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${meta.chip}`}
      data-testid={`cv-status-chip-${status}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function MerchantCheckoutVerification() {
  const [storefronts, setStorefronts] = useState([]);
  const [statusMap, setStatusMap] = useState({}); // sid -> verification doc
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { sf, current }
  const [form, setForm] = useState({ submitted_url: "", merchant_notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/merchant/storefronts");
      const list = data?.storefronts || [];
      setStorefronts(list);
      // Fetch verification per storefront (small N — same call the backend
      // makes for the gate anyway).
      const entries = await Promise.all(
        list.map(async (sf) => {
          try {
            const { data: v } = await api.get(
              `/v1/merchant/storefronts/${sf.id}/checkout-verification`
            );
            return [sf.id, v];
          } catch {
            return [sf.id, { status: "NOT_CHECKED" }];
          }
        })
      );
      setStatusMap(Object.fromEntries(entries));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load storefronts"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSubmit = (sf) => {
    const current = statusMap[sf.id] || { status: "NOT_CHECKED" };
    setForm({
      submitted_url: current.submitted_url || sf.website_url || "",
      merchant_notes: current.merchant_notes || "",
    });
    setModal({ sf, current });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!modal) return;
    setSubmitting(true);
    try {
      await api.post(
        `/v1/merchant/storefronts/${modal.sf.id}/checkout-verification`,
        {
          submitted_url: form.submitted_url.trim(),
          merchant_notes: form.merchant_notes.trim() || null,
        }
      );
      toast.success("Submitted for review");
      setModal(null);
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.message ||
        e?.response?.data?.detail ||
        "Submission failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(() => {
    const counts = { VERIFIED: 0, PENDING_REVIEW: 0, NOT_CHECKED: 0,
                     NOT_SUPPORTED: 0, GRANDFATHERED: 0 };
    for (const sf of storefronts) {
      const s = (statusMap[sf.id] || {}).status || "NOT_CHECKED";
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [storefronts, statusMap]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6"
         data-testid="merchant-checkout-verification-page">
      <header className="space-y-2">
        <div className="pf-chip pf-chip-lav w-fit">
          <ShieldCheck className="w-3 h-3" /> Phase A · Manual review
        </div>
        <h1 className="font-display text-3xl text-slate-900">
          Coupon Checkout Verification
        </h1>
        <p className="text-sm text-slate-600 max-w-3xl">
          To use Coupon Code redemption, shoppers must have a visible place
          to enter or paste a promo or coupon code during checkout. Cyannect
          reviews each storefront's checkout manually before enabling
          Coupon Code offers. Automatic Discount offers are unaffected.
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {["VERIFIED", "PENDING_REVIEW", "NOT_CHECKED", "NOT_SUPPORTED", "GRANDFATHERED"].map((s) => (
          <div key={s} className="pf-card p-3 text-center"
               data-testid={`cv-count-${s}`}>
            <div className="text-2xl font-display text-slate-900">
              {summary[s] || 0}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">
              {STATUS_META[s].label}
            </div>
          </div>
        ))}
      </div>

      <div className="pf-card divide-y divide-slate-200/60">
        {loading && (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2"
               data-testid="cv-loading">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading storefronts…
          </div>
        )}
        {!loading && storefronts.length === 0 && (
          <div className="p-8 text-sm text-slate-500 text-center"
               data-testid="cv-empty">
            No storefronts on this account yet.
          </div>
        )}
        {!loading && storefronts.map((sf) => {
          const v = statusMap[sf.id] || { status: "NOT_CHECKED" };
          const status = v.status;
          const meta = STATUS_META[status] || STATUS_META.NOT_CHECKED;
          const isRejected = status === "NOT_SUPPORTED";
          return (
            <div key={sf.id} className="p-5 flex flex-wrap gap-4 items-start"
                 data-testid={`cv-row-${sf.id}`}>
              <div className="flex-1 min-w-[220px] space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-xl text-slate-900">
                    {sf.name || sf.slug}
                  </div>
                  <StatusChip status={status} />
                </div>
                {sf.website_url && (
                  <a href={sf.website_url} target="_blank"
                     rel="noopener noreferrer"
                     className="text-xs text-slate-500 inline-flex items-center gap-1 hover:underline"
                     data-testid={`cv-website-${sf.id}`}>
                    {sf.website_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <p className="text-sm text-slate-600 max-w-2xl">
                  {meta.describe}
                </p>
                {(status === "PENDING_REVIEW" || status === "VERIFIED"
                  || status === "NOT_SUPPORTED") && v.submitted_url && (
                  <div className="text-xs text-slate-500 mt-1 space-y-0.5"
                       data-testid={`cv-submitted-${sf.id}`}>
                    <div>
                      <span className="font-semibold text-slate-600">
                        Submitted URL:
                      </span>{" "}
                      <a href={v.submitted_url} target="_blank"
                         rel="noopener noreferrer"
                         className="hover:underline break-all">
                        {v.submitted_url}
                      </a>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-600">
                        Submitted:
                      </span>{" "}
                      {fmtDate(v.submitted_at)}
                    </div>
                    {status === "VERIFIED" && v.verified_url && (
                      <div>
                        <span className="font-semibold text-emerald-700">
                          Verified URL:
                        </span>{" "}
                        <a href={v.verified_url} target="_blank"
                           rel="noopener noreferrer"
                           className="hover:underline break-all">
                          {v.verified_url}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {v.verification_reason && (status === "PENDING_REVIEW"
                  || status === "NOT_SUPPORTED") && (
                  <div className={`mt-2 rounded-xl px-3 py-2 text-xs ring-1 flex gap-2 ${
                    isRejected
                      ? "bg-rose-50 text-rose-800 ring-rose-200"
                      : "bg-amber-50 text-amber-800 ring-amber-200"
                  }`}
                       data-testid={`cv-reason-${sf.id}`}>
                    <MessagesSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold">
                        {isRejected
                          ? "Reviewer note"
                          : "More information needed"}
                      </div>
                      <div className="mt-0.5">{v.verification_reason}</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {(status === "NOT_CHECKED" || status === "GRANDFATHERED") && (
                  <button
                    type="button"
                    onClick={() => openSubmit(sf)}
                    className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                    data-testid={`cv-verify-cta-${sf.id}`}
                  >
                    <ShieldCheck className="w-4 h-4" /> Verify Checkout
                  </button>
                )}
                {status === "PENDING_REVIEW" && (
                  <button
                    type="button"
                    onClick={() => openSubmit(sf)}
                    className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5"
                    data-testid={`cv-update-submission-${sf.id}`}
                  >
                    <RefreshCw className="w-4 h-4" /> Update submission
                  </button>
                )}
                {status === "VERIFIED" && (
                  <button
                    type="button"
                    onClick={() => openSubmit(sf)}
                    className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                    data-testid={`cv-resubmit-${sf.id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Resubmit
                  </button>
                )}
                {status === "NOT_SUPPORTED" && (
                  <>
                    <Link
                      to="/merchant/campaigns"
                      className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                      data-testid={`cv-use-auto-discount-${sf.id}`}
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Use Automatic Discount
                    </Link>
                    <button
                      type="button"
                      onClick={() => openSubmit(sf)}
                      className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                      data-testid={`cv-resubmit-${sf.id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Resubmit
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={() => !submitting && setModal(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                className="glass-strong rounded-3xl w-full max-w-lg p-6"
                data-testid="cv-submit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl text-slate-900">
                Verify checkout —{" "}
                <span className="pf-gradient-text-2">{modal.sf.name}</span>
              </h3>
              <button type="button" onClick={() => !submitting && setModal(null)}
                      data-testid="cv-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Provide the URL where shoppers reach your cart or checkout,
              along with any instructions to help Cyannect locate the
              coupon-entry field.
            </p>

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">
              Cart / checkout URL *
            </label>
            <input
              required type="url"
              value={form.submitted_url}
              onChange={(e) => setForm({ ...form, submitted_url: e.target.value })}
              placeholder="https://yourshop.com/cart"
              className="pf-input w-full px-3 py-2 mt-1 text-sm font-mono"
              data-testid="cv-input-url"
            />

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">
              Instructions (optional)
            </label>
            <textarea
              value={form.merchant_notes} rows={4}
              onChange={(e) => setForm({ ...form, merchant_notes: e.target.value })}
              placeholder='e.g. "Add an item to the cart, then click Have a promo code?"'
              className="pf-input w-full px-3 py-2 mt-1 text-sm"
              maxLength={2000}
              data-testid="cv-input-notes"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Screenshot upload is coming soon — for now, a URL and written
              instructions are all we need.
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button"
                      onClick={() => !submitting && setModal(null)}
                      className="pf-btn-glass px-4 py-2 rounded-full text-sm">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                      className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-60"
                      data-testid="cv-submit">
                {submitting ? "Submitting…" : "Submit for review"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
